<script>
export const SUBSTANCE_USE_SCHEMA = {
    widgetType: 'substanceUseList',
    label: 'Substance Use History',
    fields: [
        { name: 'name', type: 'string', required: true, description: 'Substance name (e.g. Alcohol)' },
        {
            name: 'route',
            type: 'choice',
            required: false,
            description: 'Route of administration',
            options: [
                { value: 'By mouth', label: 'By mouth' },
                { value: 'Inhale (e.g., smoke vape)', label: 'Inhale (e.g., smoke vape)' },
                { value: 'Through nose', label: 'Through nose' },
                { value: 'Injection', label: 'Injection' },
                { value: 'Applied to skin', label: 'Applied to skin' },
                { value: 'Under tongue', label: 'Under tongue' },
                { value: 'Patch', label: 'Patch' },
            ],
        },
        { name: 'dose', type: 'string', required: false, description: 'Amount/dose and frequency' },
    ],
};
</script>

<script setup>
import { ref, computed, inject, onBeforeUnmount, onMounted, watch } from 'vue';
import BnDatePicker from '@/components/BnDatePicker.vue';
import BnMenu from '@/components/BnMenu.vue';
import BnMenuItem from '@/components/BnMenuItem.vue';
import BnDialog from '@/components/BnDialog.vue';
import DataProvider from '@/components/DataProvider';
import { MedicationStatement, List } from '@/fhirworks';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useDisplay } from 'vuetify';
import { validateChoiceFields } from '@/components/questionnaires/widgets/widgetValidation';

const useHistoryItems = [
    { value: 'active', title: 'Active' },
    { value: 'stopped', title: 'Stopped' },
    { value: 'not-taken', title: 'None' },
];

const routeItems = [
    { value: 'By mouth', code: '26643006' },
    { value: 'Inhale (e.g., smoke vape)', code: '446406008' },
    { value: 'Through nose', code: '46713006' },
    { value: 'Injection', code: '47625008' },
    { value: 'Applied to skin', code: '6064005' },
    { value: 'Under tongue', code: '37839007' },
    { value: 'Patch', code: '45890007' },
];

const patternOfUseItems = [
    { value: 'binge', title: 'Binge' },
    { value: 'continuous', title: 'Continuous' },
    { value: 'episodic', title: 'Episodic' },
];

const vuetifyDisplay = useDisplay();

const props = defineProps({
    title: String,
    patientId: String,
    printView: Boolean,
    isLocked: Boolean,
    modelValue: [Array, Object, String],
});

const viewOnly = computed(() => props.isLocked || props.printView);

const emit = defineEmits(['update:modelValue']);

const bnDialogRef = ref(null);
const substanceUseDP = ref(null);
const substanceUseData = ref([]);

const substanceUseQuery = computed(() => {
    if (props.isLocked && props.modelValue) {
        return { resource: props.modelValue };
    }

    return {
        query: [
            { query: `subject=${props.patientId}`, resourceType: 'MedicationStatement' },
            { query: `subject=${props.patientId}&code=substanceUseHistory`, resourceType: 'List' },
        ],
    };
});

const activeSubstances = computed(() => {
    return substanceUseData.value.filter((resource) => resource.resourceType === 'MedicationStatement');
});

const hasSubstanceUse = ref(null);

watch(
    () => hasSubstanceUse.value,
    () => {
        if (hasSubstanceUse.value === true) {
            setSubstanceUseTrue();
        } else if (hasSubstanceUse.value === false) {
            removeSubstanceUseData();
        }
    },
);

watch(
    () => substanceUseData.value,
    () => {
        if (substanceUseData.value && !props.isLocked) {
            emit('update:modelValue', substanceUseData.value);
        }
    },
    { immediate: true, deep: true },
);

// what to do when the overdoseInstance changes between Yes and No
const updateOverdoseInstance = (substance) => {
    if (substance.overdoseInstance) return;
    if (!substance.overdoseInstanceDetails) return;
    const options = {
        header: 'Delete',
        message: 'Are you sure you want to delete the instances of overdose information?',
        confirm: 'Yes',
        cancel: 'No',
    };
    // allows the details box to remain open while user confirms or cancels deletion
    substance.showOverdoseDetailsBox = true;
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) {
            substance.overdoseInstance = true;
        } else {
            substance.overdoseInstanceDetails = '';
        }
        substance.showOverdoseDetailsBox = false;
    });
};

// what to do when withdrawalSymptoms changes between Yes and No
const updateWithdrawalSymptoms = (substance) => {
    if (substance.withdrawalSymptoms) {
        // Do nothing if withdrawalSymptoms is toggled to "Yes"
        return;
    }

    // Check if we need to show the confirmation dialog
    const shouldShowDialog = (substance.speakWithProvider !== null && substance.speakWithProvider !== undefined) || (substance.symptoms && substance.symptoms.trim() !== '');

    if (!shouldShowDialog) {
        // If no content in symptoms and speakWithProvider has no value, clear them directly without prompting
        substance.symptoms = '';
        substance.speakWithProvider = '';
        return;
    }

    // Prompt the user if withdrawalSymptoms is toggled to "No" and the conditions are met
    const options = {
        header: 'Delete',
        message: 'Are you sure you want to delete the withdrawal symptoms information?',
        confirm: 'Yes',
        cancel: 'No',
    };

    // Show the symptoms details and speak with provider options during confirmation
    substance.showSymptomsDetailsBox = true;
    substance.showSpeakWithProviderBox = true;

    bnDialogRef.value.show(options).then(async (confirm) => {
        if (confirm) {
            // Clear symptoms and speakWithProvider if the user confirms
            substance.symptoms = '';
            substance.speakWithProvider = '';
        } else {
            // Revert withdrawalSymptoms to true if the user cancels
            substance.withdrawalSymptoms = true;
        }

        // Hide the symptoms details and speak with provider options after confirmation
        substance.showSymptomsDetailsBox = false;
        substance.showSpeakWithProviderBox = false;
    });
};

const substanceUseLoaded = async () => {
    if (substanceUseData.value.length) {
        hasSubstanceUse.value = !substanceUseData.value.find((substanceUse) => substanceUse.resourceType === 'List' && substanceUse.code?.coding[0].code === 'substanceUseHistory');
    }
};

const addEmptySubstanceUse = () => {
    if (!substanceUseDP.value?.addItem) return;

    let emptyMedicationStatement = new MedicationStatement({
        subject: { id: props.patientId, resourceType: 'Patient' },
        status: 'active',
        medication: {
            CodeableConcept: {
                text: '',
            },
        },
        dateAsserted: new Date().toISOString(),
    });

    substanceUseDP.value.addItem(emptyMedicationStatement);
};

const setSubstanceUseTrue = async () => {
    let substanceUseLists = substanceUseData.value.filter((list) => list.resourceType === 'List' && list.code?.codeZero === 'substanceUseHistory');

    substanceUseLists.forEach((list) => {
        substanceUseDP.value.deleteItem(list);
    });

    if (!substanceUseData.value.find((resource) => resource.resourceType === 'MedicationStatement')) {
        addEmptySubstanceUse();
    }
};

const removeSubstanceUseData = async () => {
    let emptyList = new List({
        subject: { id: props.patientId, resourceType: 'Patient' },
        status: 'current',
        mode: 'working',
        emptyReason: {
            coding: [
                {
                    system: 'http://terminology.hl7.org/CodeSystem/list-empty-reason',
                    code: 'nilknown',
                },
            ],
        },
        code: {
            coding: [
                {
                    code: 'substanceUseHistory',
                    system: 'https://bestnotes.com',
                },
            ],
        },
    });
    // Skip dialog if no saved substance use exists
    // Also skip dialog if there's only 1 substance with blank name
    const hasNoSavedSubstances = !substanceUseData.value.find((resource) => resource.resourceType === 'MedicationStatement' && resource?.id);
    const hasSingleBlankSubstance = activeSubstances.value.length === 1 && !activeSubstances.value[0]?.medication?.CodeableConcept?.text;

    if (hasNoSavedSubstances || hasSingleBlankSubstance) {
        for (let resource of substanceUseData.value) {
            substanceUseDP.value.deleteItem(resource);
        }
        await substanceUseDP.value.save(emptyList);
        return;
    }

    const options = {
        header: 'Warning',
        message: 'This will delete all substance use history, continue?',
        confirm: 'Delete',
        cancel: 'Cancel',
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) {
            hasSubstanceUse.value = true;
            return;
        }

        for (let resource of substanceUseData.value) {
            substanceUseDP.value.deleteItem(resource);
        }
        await substanceUseDP.value.save(emptyList);
    });
};

const deleteMedicationStatement = (substance) => {
    const options = {
        header: 'Delete',
        message: 'Are you sure you want to delete this substance use?',
        confirm: 'Yes',
        cancel: 'No',
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) return;
        await substanceUseDP.value.deleteItem(substance);

        if (!substanceUseData.value.length) {
            addEmptySubstanceUse();
        }
    });
};

// Save the updated speakWithProvider value to the data provider
const updateSpeakWithProvider = (substance, value) => {
    substance.speakWithProvider = value;
    substanceUseDP.value.save(substance); // Save to the data provider
};

const updateRoute = (substance, route) => {
    let routeItem = { text: route };

    let routeCode = routeItems.find((item) => item.value === route)?.code;

    if (routeCode) {
        routeItem.coding = [
            {
                system: 'http://snomed.info/sct',
                code: routeCode,
                display: route,
            },
        ];
    }

    if (substance.dosage?.[0]) {
        substance.dosage[0].route = routeItem;
        return;
    }

    substance.dosage = [{ route: routeItem }];
};

const updateDose = (substance, dose) => {
    if (substance.dosage?.[0]) {
        substance.dosage[0].text = dose;
        return;
    }

    substance.dosage = [{ text: dose }];
};

const addItem = async (data) => {
    const invalidChoices = validateChoiceFields(data, SUBSTANCE_USE_SCHEMA);
    if (invalidChoices) return invalidChoices;

    const substance = new MedicationStatement({
        subject: { id: props.patientId, resourceType: 'Patient' },
        status: 'active',
        medication: { CodeableConcept: { text: data.name || '' } },
        dateAsserted: new Date().toISOString(),
    });
    if (data.route) {
        const routeItem = routeItems.find((r) => r.value === data.route);
        if (routeItem) {
            substance.dosage = [{ route: { text: data.route, coding: [{ system: 'http://snomed.info/sct', code: routeItem.code, display: data.route }] } }];
        } else {
            substance.dosage = [{ route: { text: data.route } }];
        }
    }
    if (data.dose) {
        substance.dosage = substance.dosage || [{}];
        substance.dosage[0].text = data.dose;
    }
    await substanceUseDP.value.save(substance);

    const nilKnownLists = substanceUseData.value.filter((r) => r.resourceType === 'List' && r.code?.codeZero === 'substanceUseHistory');
    for (const list of nilKnownLists) {
        await substanceUseDP.value.deleteItem(list);
    }

    hasSubstanceUse.value = true;
    return { success: true };
};

const getItems = () => {
    return activeSubstances.value
        .filter((s) => s.medication?.CodeableConcept?.text)
        .map((s) => ({
            name: s.medication?.CodeableConcept?.text || '',
            route: s.dosage?.[0]?.route?.text || '',
            dose: s.dosage?.[0]?.text || '',
            status: s.status || '',
        }));
};

defineExpose({ addItem, getItems });

const _registerWidgetApi = inject('registerWidgetApi', null);
const _unregisterWidgetApi = inject('unregisterWidgetApi', null);

onMounted(() => {
    _registerWidgetApi?.('substanceUseList', { addItem, getItems, schema: SUBSTANCE_USE_SCHEMA });
});

onBeforeUnmount(() => {
    _unregisterWidgetApi?.('substanceUseList');
});

const setMedicationCode = function (substance, item) {
    if (typeof item === 'string' && item) {
        substance.medication.CodeableConcept.text = item;
    }

    if (!item) return;
    if (item.code) {
        substance.medication.CodeableConcept.coding = [
            {
                code: item.code,
                system: 'http://www.nlm.nih.gov/research/umls/rxnorm',
                display: item.display,
            },
        ];
        substance.medication.CodeableConcept.text = item.display;
    }
};
</script>

<template>
    <v-card variant="flat" :border="false" class="pb-2">
        <DataProvider ref="substanceUseDP" v-model="substanceUseData" collection autosave refresh-on-change v-bind="substanceUseQuery" @loaded="substanceUseLoaded">
            <template #loading>
                <v-skeleton-loader class="mx-auto" width="100%" type="list-item-three-line" />
            </template>
            <template #error="{ error, refresh }">
                <div>
                    {{ error }}
                    <v-btn @click="refresh">Try Again</v-btn>
                </div>
            </template>
            <template #default>
                <div data-cy="substanceUseList" data-linkid="substanceUseList">
                    <div class="d-flex py-1 text-h2" style="color: #43a047">
                        <FontAwesomeIcon size="lg" :icon="['far', 'sack-xmark']" class="align-self-center mr-2"></FontAwesomeIcon>
                        <v-list-item-title class="text-h2">{{ title }}</v-list-item-title>
                    </div>

                    <v-radio-group
                        v-model="hasSubstanceUse"
                        :readonly="viewOnly"
                        label="Do you have any history of illicit or problematic substance use?"
                        inline
                        class="mt-0 mb-4"
                        hint="Include any alcohol, nicotine, and illicit or problematic use of prescription drugs or over-the-counter medications."
                        persistent-hint
                    >
                        <v-radio label="Yes" :value="true" class="mr-2" data-cy="hasSubstanceUseTrueRadio"></v-radio>
                        <v-radio label="No" :value="false" data-cy="hasSubstanceUseFalseRadio"></v-radio>
                    </v-radio-group>
                    <template v-for="(substance, index) in activeSubstances" :key="index">
                        <div style="border-left: 3px solid #43a047; opacity: 1; padding-left: 8px" class="mb-3 pt-2">
                            <v-row no-gutters>
                                <v-col cols="12" sm="6">
                                    <div class="d-flex align-items-center">
                                        <v-text-field
                                            :model-value="substance.medication?.CodeableConcept.text"
                                            :readonly="viewOnly"
                                            class="text-truncate flex-grow-1 mr-2"
                                            :style="vuetifyDisplay.mdAndUp.value ? 'flex-basis: auto;' : 'flex-basis: 80%'"
                                            label="Substance"
                                            data-cy="medicationField"
                                            @update:model-value="(item) => setMedicationCode(substance, item)"
                                        >
                                        </v-text-field>
                                        <BnMenu v-if="!viewOnly && !vuetifyDisplay.smAndUp.value" offset-y left icon="ellipsis-v" class="d-flex align-items-center" data-cy="substanceItemMenu">
                                            <BnMenuItem icon="trash" label="Delete substance" data-cy="removeSubstanceBtn" @click="deleteMedicationStatement(substance)" />
                                        </BnMenu>
                                    </div>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <div class="d-flex align-items-center">
                                        <v-select
                                            v-model="substance.status"
                                            label="Use history"
                                            :items="useHistoryItems"
                                            :data-options="JSON.stringify(useHistoryItems)"
                                            :disabled="!substance.medication?.CodeableConcept.text"
                                            :readonly="viewOnly"
                                            data-cy="useHistorySelect"
                                            class="mr-2"
                                        />
                                        <BnMenu v-if="!viewOnly && vuetifyDisplay.smAndUp.value" offset-y left icon="ellipsis-v" class="d-flex align-items-center" data-cy="substanceItemMenu">
                                            <BnMenuItem icon="trash" label="Delete substance" data-cy="removeSubstanceBtn" @click="deleteMedicationStatement(substance)" />
                                        </BnMenu>
                                    </div>
                                </v-col>
                            </v-row>

                            <v-row no-gutters>
                                <v-col cols="12">
                                    <div class="d-flex flex-grow-1">
                                        <v-text-field
                                            :model-value="substance.dosage?.[0]?.text"
                                            :disabled="!substance.medication?.CodeableConcept.text"
                                            :readonly="viewOnly"
                                            class="mr-2"
                                            label="Amount/dose and how often"
                                            data-cy="doseField"
                                            @update:model-value="(val) => updateDose(substance, val)"
                                        ></v-text-field>
                                    </div>
                                </v-col>
                            </v-row>

                            <v-row no-gutters>
                                <v-col cols="12" sm="6" md="3">
                                    <div class="d-flex flex-grow-1">
                                        <v-combobox
                                            :model-value="substance.dosage?.[0]?.route?.text"
                                            :disabled="!substance.medication?.CodeableConcept.text"
                                            :readonly="viewOnly"
                                            :items="routeItems.map((item) => item.value)"
                                            :data-options="JSON.stringify(routeItems.map((item) => item.value))"
                                            class="mr-2"
                                            label="Route"
                                            data-cy="routeField"
                                            @update:model-value="(val) => updateRoute(substance, val)"
                                        ></v-combobox>
                                    </div>
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <!-- Pattern of use dropdown-->
                                    <div class="d-flex flex-grow-1">
                                        <v-select
                                            v-model="substance.patternOfUse"
                                            label="Pattern of use"
                                            :items="patternOfUseItems"
                                            :data-options="JSON.stringify(patternOfUseItems)"
                                            :disabled="!substance.medication?.CodeableConcept.text"
                                            class="mr-2"
                                            :readonly="viewOnly"
                                        ></v-select>
                                    </div>
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <div class="flex-grow-1">
                                        <BnDatePicker
                                            v-model="substance.dateOfLastUse"
                                            label="Date of last use"
                                            data-cy="dateOfLastUseField"
                                            :disabled="!substance.medication?.CodeableConcept.text"
                                            :readonly="viewOnly"
                                            :strict-date="false"
                                            class="mr-2"
                                        ></BnDatePicker>
                                    </div>
                                </v-col>
                                <v-col cols="12" sm="6" md="3">
                                    <div class="d-flex flex-grow-1">
                                        <v-text-field
                                            v-model="substance.firstUsedAge"
                                            label="Age when first used"
                                            data-cy="firstUsedAgeField"
                                            :disabled="!substance.medication?.CodeableConcept.text"
                                            class="mr-2"
                                            :readonly="viewOnly"
                                        ></v-text-field>
                                    </div>
                                </v-col>
                            </v-row>
                            <div class="d-flex w-100 mt-2">
                                <div class="d-flex ml-2">
                                    <v-checkbox v-model="substance.drugOfChoice" label="Drug of choice" :disabled="!substance.medication?.CodeableConcept.text" :readonly="viewOnly"></v-checkbox>
                                </div>
                            </div>
                            <div class="d-flex flex-wrap mr-2">
                                <v-text-field
                                    v-model="substance.obtained"
                                    :disabled="!substance.medication?.CodeableConcept.text"
                                    :readonly="viewOnly"
                                    class="flex-grow-1"
                                    label="Means of acquisition"
                                    data-cy="obtainedField"
                                ></v-text-field>
                            </div>
                            <div class="d-flex flex-wrap">
                                <v-radio-group
                                    v-model="substance.overdoseInstance"
                                    label="Any instances of overdose?"
                                    inline
                                    class="mt-0 mb-2"
                                    :disabled="!substance.medication?.CodeableConcept.text"
                                    :readonly="viewOnly"
                                    @update:model-value="updateOverdoseInstance(substance)"
                                >
                                    <v-radio label="Yes" :value="true" class="mr-2"></v-radio>
                                    <v-radio label="No" :value="false"></v-radio>
                                </v-radio-group>
                            </div>
                            <v-textarea
                                v-show="substance.overdoseInstance || substance.showOverdoseDetailsBox"
                                v-model="substance.overdoseInstanceDetails"
                                label="Details"
                                rows="1"
                                auto-grow
                                :disabled="!substance.medication?.CodeableConcept.text"
                                :readonly="viewOnly"
                            />
                            <div class="d-flex flex-wrap">
                                <v-radio-group
                                    v-model="substance.withdrawalSymptoms"
                                    label="Are you experiencing or have you experienced any symptoms of withdrawal in the last 12 months?"
                                    inline
                                    class="mt-0 mb-2"
                                    :disabled="!substance.medication?.CodeableConcept.text"
                                    :readonly="viewOnly"
                                    @update:model-value="updateWithdrawalSymptoms(substance)"
                                >
                                    <v-radio label="Yes" :value="true" class="mr-2" data-cy="hasWithdrawalSymptomsTrueRadio"></v-radio>
                                    <v-radio label="No" :value="false" data-cy="hasWithdrawalSymptomsFalseRadio"></v-radio>
                                </v-radio-group>
                            </div>
                            <v-text-field
                                v-show="substance.withdrawalSymptoms || substance.showSymptomsDetailsBox"
                                v-model="substance.symptoms"
                                :disabled="!substance.medication?.CodeableConcept.text"
                                :readonly="viewOnly"
                                class="flex-grow-1"
                                label="Details"
                                data-cy="symptomField"
                            ></v-text-field>

                            <v-radio-group
                                v-show="substance.withdrawalSymptoms || substance.showSpeakWithProviderBox"
                                v-model="substance.speakWithProvider"
                                :disabled="!substance.medication?.CodeableConcept.text"
                                :readonly="viewOnly"
                                inline
                                class="flex-grow-1"
                                label="Would you like to speak with a provider about taking medication to assist with drug/alcohol withdrawal symptoms?"
                                @update:model-value="(val) => updateSpeakWithProvider(substance, val)"
                            >
                                <v-radio label="Yes" :value="true" class="mr-2" data-cy="speakWithProviderTrueRadio"></v-radio>
                                <v-radio label="No" :value="false" data-cy="speakWithProviderFalseRadio"></v-radio>
                            </v-radio-group>
                        </div>
                    </template>
                    <v-btn v-if="hasSubstanceUse && !viewOnly" color="primary" variant="tonal" size="x-small" class="ml-2" @click="addEmptySubstanceUse">
                        <template #prepend>
                            <FontAwesomeIcon :icon="['far', 'plus']" size="sm" />
                        </template>
                        Add another substance
                    </v-btn>
                </div>
            </template>
        </DataProvider>
        <BnDialog ref="bnDialogRef" />
    </v-card>
</template>
